import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {environment} from '../../../../environments/environment';
import {catchError, Observable, shareReplay, tap, throwError} from 'rxjs';
import {Departament, DepartamentPost} from './departament.interface';

@Injectable({
  providedIn: 'root'
})
export class DepartamentService {
  public http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/clients`;
  private path = '/departments';

  // Cache por cliente
  private cache = new Map<string, Observable<Departament[]>>();

  getDepartaments(idClient: string): Observable<Departament[]> {
    if (!idClient) return throwError(() => new Error('Client ID is required'));

    const cached = this.cache.get(idClient);
    if (cached) return cached;

    const req$ = this.http.get<Departament[]>(`${this.apiUrl}/${idClient}${this.path}`)
      .pipe(
        catchError(this.handleError),
        shareReplay(1)
      );

    this.cache.set(idClient, req$);
    return req$;
  }

  // Método para invalidar cache cuando se crean/actualizan/eliminen departamentos
  invalidateCache(idClient: string): void {
    this.cache.delete(idClient);
  }

  getDepartament(id: number, idClient: string): Observable<Departament> {
    return this.http.get<Departament>(`${this.apiUrl}/${idClient}${this.path}/${id}`)
      .pipe(catchError(this.handleError));
  }

  createDepartament(idClient: string, departament: DepartamentPost): Observable<Departament> {
    return this.http.post<Departament>(`${this.apiUrl}/${idClient}${this.path}`, departament)
      .pipe(
        catchError(this.handleError),
        tap(() => this.invalidateCache(idClient)) // Invalidar cache después de crear
      );
  }

  updateDepartament(id: string, departament: Departament, idClient: string): Observable<Departament> {
    return this.http.put<Departament>(`${this.apiUrl}/${idClient}${this.path}/${id}`, departament)
      .pipe(
        catchError(this.handleError),
        tap(() => this.invalidateCache(idClient)) // Invalidar cache después de actualizar
      );
  }

  deleteDepartament(id: number, idClient: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${idClient}${this.path}/${id}`)
      .pipe(
        catchError(this.handleError),
        tap(() => this.invalidateCache(idClient)) // Invalidar cache después de eliminar
      );
  }

  createDepartmentsBatch(clientId: string, items: { name: string }[]) {
    return this.http.post<Departament[]>(
      `${this.apiUrl}/${clientId}/departments/batch`,
      items
    ).pipe(
      tap(() => this.invalidateCache(clientId)) // Invalidar cache después de crear batch
    );
  }


  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMsg = 'Ocurrió un error inesperado';
    if (error.error && error.error.message) {
      errorMsg = error.error.message;
    }
    return throwError(() => new Error(errorMsg));
  }
}
